import { formatDateToDDMMYYYY, formatDateToYYYYMMDD } from "../../utils/dateUtils";
import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosConfig';
import { MdArrowBack } from 'react-icons/md';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components defined outside to prevent re-mounting on every render
const InputField = ({ label, name, type = "text", required = false, placeholder = "", value, onChange }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input 
            type={type} 
            name={name} 
            required={required} 
            value={value ?? ''}
            onChange={onChange} 
            placeholder={placeholder}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" 
        />
    </div>
);

const SelectField = ({ label, name, options, required = false, value, onChange }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <select 
            name={name} 
            required={required} 
            value={value ?? ''}
            onChange={onChange} 
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
        >
            <option value="">Select {label}</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    </div>
);

const EditMember = ({ memberId, onBack, onUpdate }) => {
    const [formData, setFormData] = useState({
        member_name: '',
        member_id: '',
        member_code: '',
        member_type: 'MEMBER', 
        member_type_choice: 'Journalist', 
        member_status: 'ACTIVE',

        leadership_place: '',
        leadership_designation: '',
        leadership_wing: '',
        
        registered_on: '',
        valid_till: '',
        last_renewed_on: '',
        renewal: 'Not Renewed',
        
        mobile: '',
        whatsapp_no: '',
        email: '',
        member_address: '',
        
        referrer: null,
        referrer_name: '',
        referrer_mobile_number: '',
        
        state: '',
        district: '',
        city: 'Not Applicable',
        sub_district: 'Not Applicable',
        zone: 'Not Applicable',
        areaname: '',
        taluk: 'Not Applicable',
        village: 'Not Applicable',
        ward: '',
        pin_code: '',
        police_station: '',
        
        gender: '',
        date_of_birth: '',
        religion: '',
        
        voter_id: '',
        aadhar_number: '',
        occupation: '',
        business: '',
        business_designation: '',
        remarks: '',
        is_association_ambassador: false,

        // Payment Details (For adding NEW payments)
        membership_amount: '',
        membership_payment_date: '',
        leadership_amount: '',
        leadership_payment_date: '',
        slug: '',
    });
    // Dynamic Renewals List
    const [renewals, setRenewals] = useState([]); 
    // Existing Payments (Read Only)
    const [existingPayments, setExistingPayments] = useState([]);
    const [picture, setPicture] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null);

    // Referral Autocomplete State
    const [allMembers, setAllMembers] = useState([]);
    const [referralSuggestions, setReferralSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Fetch all members for referral search once on load
    useEffect(() => {
        const fetchAllMembers = async () => {
            try {
                const response = await axios.get('nai-members/members/');
                setAllMembers(response.data);
            } catch (error) {
                console.error("Error fetching all members for referral:", error);
            }
        };
        fetchAllMembers();
    }, []);

    const handleReferralSearch = (query) => {
        setFormData({ ...formData, referrer_name: query });
        if (!query.trim()) {
            setReferralSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const lowerQuery = query.toLowerCase();
        const matches = allMembers.filter(member => 
            (member.member_name && member.member_name.toLowerCase().includes(lowerQuery)) ||
            (member.member_id && String(member.member_id).padStart(4, '0').includes(lowerQuery)) ||
            (member.mobile && String(member.mobile).includes(lowerQuery))
        );
        
        setReferralSuggestions(matches);
        setShowSuggestions(true);
    };

    const selectReferrer = (member) => {
        setFormData({
            ...formData,
            referrer: member.id,
            referrer_name: member.member_name,
            referrer_mobile_number: member.mobile
        });
        setShowSuggestions(false);
    };

    const indianStates = [
        "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", 
        "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", 
        "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", 
        "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", 
        "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", 
        "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", 
        "Lakshadweep", "Puducherry"
    ];

    const memberTypeChoices = ["MEMBER", "LEADER"];


    const placeChoices = [
        "National", 
        "National Regional - Northern", "National Regional - Southern", 
        "National Regional - North Western", "National Regional - North Eastern", 
        "National Regional - Eastern", "National Regional - Central", 
        "National Regional - Western",
        "State", 
        "State Regional - North", "State Regional - South", "State Regional - West", "State Regional - East",
        "District", "Sub - District", "Constituency", "Zone", "Taluk", "Ward"
    ];

    const [designationChoices, setDesignationChoices] = useState([
        "Patron", "Vice Patron", "Honorary President", "President", "Vice President",
        "General Secretary", "Joint Gen. Secretary", "Treasurer", "Ambassador",
        "Organizing Secretary", "Media Secretary", "Political Secretary",
        "Propaganda Secretary", "Legal Secretary",
        "Action Committee Member - 1", "Action Committee Member - 2",
        "Action Committee Member - 3", "Action Committee Member - 4",
        "Action Committee Member - 5", "Action Committee Member - 6",
        "Action Committee Member - 7"
    ]);

    const [memberRoleChoices, setMemberRoleChoices] = useState([
        "Citizen Reporter & Leader", 
        "Journalist & Leader",
        "Journalist", "District Reporter", "State Reporter", "Chief Reporter", 
        "Citizen Reporter", "Digital Reporter", "Mobile Reporter", "News Informer", "Volunteers"
    ]);

    const [wingChoices, setWingChoices] = useState([]);

    useEffect(() => {
        const fetchWingsAndDesignations = async () => {
            try {
                const response = await axios.get('nai-members/wings/');
                setWingChoices(response.data.map(w => w.name.toUpperCase()));
            } catch (error) {
                console.error("Error fetching wings:", error);
            }

            try {
                const desgsRes = await axios.get('nai-members/designations/');
                if (desgsRes.data && desgsRes.data.length > 0) {
                    const desgs = desgsRes.data.filter(d => d.category === 'designation').map(d => d.name);
                    const rls = desgsRes.data.filter(d => d.category === 'role').map(d => d.name);
                    if (desgs.length > 0) setDesignationChoices(desgs);
                    if (rls.length > 0) setMemberRoleChoices(rls);
                }
            } catch (error) {
                console.error("Error fetching designations:", error);
            }
        };
        fetchWingsAndDesignations();
    }, []);

    const memberStatusChoices = ["ACTIVE", "EXPIRED", "DIED", "DISMISSED", "SUSPENDED", "UNUSED"];
    const genderChoices = ["Male", "Female", "Other"];
    const renewalChoices = ["Renewed", "Not Renewed"];
    const knowUsFromChoices = ["Online", "Face Book", "Whatsapp", "Instagram"];

    const formatDateForFrontend = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateStr;
    };

    const formatDateForBackend = (dateStr) => {
        if (!dateStr) return null;
        if (dateStr.includes('-')) {
            const parts = dateStr.split('-');
            if (parts[0].length === 4) {
                return `${parts[0]}-${parts[1]}-${parts[2]}`;
            }
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        if (dateStr.includes('/')) {
            const parts = dateStr.split('/');
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return dateStr;
    };

    // Helper to format payment type
    const formatPaymentType = (type) => {
        if (type === 'registration') return 'Membership Fee';
        if (type === 'leadership') return 'Leadership Fee';
        if (type === 'renewal') return 'Renewal Fee';
        if (type === 'donation') return 'Donation';
        return type ? type.toUpperCase() : 'Fee';
    };

    const feeCategoryOptions = [
        { label: 'Membership', value: 'registration' },
        { label: 'Leadership', value: 'leadership' },
        { label: 'Renewal', value: 'renewal' },
        { label: 'Donation', value: 'donation' }
    ];

    const paymentStatusTypeOptions = [
        { label: 'Full Payment', value: 'full' },
        { label: 'Part Payment', value: 'part' }
    ];

    const paymentModeOptions = [
        'Cash',
        'UPI',
        'Bank Transfer',
        'Credit/Debit Card',
        'Cheque',
        'Other'
    ];

    const [newPaymentForm, setNewPaymentForm] = useState({
        payment_type: 'registration',
        payment_status_type: 'full',
        amount: '',
        total_amount: '',
        balance_amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_mode: 'UPI',
        notes: ''
    });
    const [addingPayment, setAddingPayment] = useState(false);

    const handleNewPaymentChange = (field, value) => {
        setNewPaymentForm(prev => {
            const item = { ...prev, [field]: value };
            if (field === 'payment_status_type') {
                if (value === 'full') {
                    item.total_amount = '';
                    item.balance_amount = '';
                } else {
                    const tot = parseFloat(item.total_amount) || 0;
                    const amt = parseFloat(item.amount) || 0;
                    item.balance_amount = item.total_amount ? Math.max(0, tot - amt).toFixed(2) : '';
                }
            } else if (field === 'total_amount' || field === 'amount') {
                if (item.payment_status_type === 'part') {
                    const tot = field === 'total_amount' ? (parseFloat(value) || 0) : (parseFloat(item.total_amount) || 0);
                    const amt = field === 'amount' ? (parseFloat(value) || 0) : (parseFloat(item.amount) || 0);
                    const hasTotal = field === 'total_amount' ? !!value : !!item.total_amount;
                    item.balance_amount = hasTotal ? Math.max(0, tot - amt).toFixed(2) : '';
                }
            }
            return item;
        });
    };

    const handleAddPaymentDirect = async (e) => {
        if (e) e.preventDefault();

        if (!newPaymentForm.amount || parseFloat(newPaymentForm.amount) <= 0) {
            toast.error("Please enter a valid amount paid.");
            return;
        }

        if (newPaymentForm.payment_status_type === 'part' && (!newPaymentForm.total_amount || parseFloat(newPaymentForm.total_amount) <= 0)) {
            toast.error("Please enter the total amount for part payment.");
            return;
        }

        setAddingPayment(true);

        try {
            const payload = {
                member: formData.id || memberId,
                payment_type: newPaymentForm.payment_type || 'registration',
                payment_status_type: newPaymentForm.payment_status_type || 'full',
                amount: parseFloat(newPaymentForm.amount),
                total_amount: newPaymentForm.payment_status_type === 'part' && newPaymentForm.total_amount ? parseFloat(newPaymentForm.total_amount) : null,
                balance_amount: newPaymentForm.payment_status_type === 'part' && newPaymentForm.balance_amount ? parseFloat(newPaymentForm.balance_amount) : null,
                payment_date: formatDateForBackend(newPaymentForm.payment_date) || new Date().toISOString().split('T')[0],
                payment_mode: newPaymentForm.payment_mode || 'UPI',
                notes: newPaymentForm.notes || '',
                status: 'success'
            };

            const res = await axios.post('nai-members/payments/', payload);
            
            // Add new payment to existingPayments list immediately so it shows in the table
            setExistingPayments(prev => [res.data, ...prev]);

            // Update member's renewal / validity status if applicable
            if (payload.payment_type === 'registration' || payload.payment_type === 'renewal') {
                const pDate = new Date(payload.payment_date);
                const nextYear = new Date(pDate);
                nextYear.setFullYear(nextYear.getFullYear() + 1);
                
                setFormData(prev => ({
                    ...prev,
                    last_renewed_on: formatDateForFrontend(payload.payment_date),
                    valid_till: formatDateForFrontend(nextYear.toISOString().split('T')[0]),
                    renewal: 'Renewed'
                }));
            }

            // Show toaster message
            toast.success("Payment added successfully!");

            // Reset form
            setNewPaymentForm({
                payment_type: 'registration',
                payment_status_type: 'full',
                amount: '',
                total_amount: '',
                balance_amount: '',
                payment_date: new Date().toISOString().split('T')[0],
                payment_mode: 'UPI',
                notes: ''
            });
        } catch (error) {
            console.error("Error adding payment:", error);
            const errMsg = error.response?.data ? JSON.stringify(error.response.data) : "Failed to add payment.";
            toast.error(errMsg);
        } finally {
            setAddingPayment(false);
        }
    };

    useEffect(() => {
        const fetchMember = async () => {
            try {
                const response = await axios.get(`nai-members/members/${memberId}/`);
                const data = response.data;
                
                // Helper to match backend value with frontend options case-insensitively
                const getCorrectCasing = (value, options) => {
                    if (!value) return '';
                    const match = options.find(opt => opt.toUpperCase() === value.toUpperCase());
                    return match || value; // Return original value if no match in options (user might have manual entry)
                };

                // Map of fields and their respective option lists
                const fieldOptionsMap = {
                    'state': indianStates,
                    'member_type': memberTypeChoices,
                    'member_type_choice': memberRoleChoices,
                    'member_status': memberStatusChoices,
                    'gender': genderChoices,
                    'leadership_place': placeChoices,
                    'leadership_designation': designationChoices,
                    'renewal': renewalChoices,
                    'know_us_from': knowUsFromChoices
                };

                // Apply casing fixes
                Object.keys(fieldOptionsMap).forEach(field => {
                    if (data[field]) {
                        data[field] = getCorrectCasing(data[field], fieldOptionsMap[field]);
                    }
                });

                // Format dates for frontend
                const dateFields = ['registered_on', 'valid_till', 'last_renewed_on', 'date_of_birth'];
                dateFields.forEach(field => {
                    if (data[field]) {
                        data[field] = formatDateForFrontend(data[field]);
                    }
                });

                setFormData(data);
                if (data.payments) {
                    setExistingPayments(data.payments);
                }
                setLoading(false);
            } catch (error) {
                console.error("Error fetching member:", error);
                setStatus({ type: 'error', message: 'Failed to load member details.' });
                setLoading(false);
            }
        };

        if (memberId) {
            fetchMember();
        }
    }, [memberId]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        setPicture(e.target.files[0]);
    };

    // Renewal Handlers
    const addRenewal = () => {
        setRenewals([...renewals, { amount: '', payment_date: '' }]);
    };

    const removeRenewal = (index) => {
        const newRenewals = [...renewals];
        newRenewals.splice(index, 1);
        setRenewals(newRenewals);
    };

    const handleRenewalChange = (index, field, value) => {
        const newRenewals = [...renewals];
        newRenewals[index][field] = value;
        setRenewals(newRenewals);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setStatus(null);

        const data = new FormData();
        
        const formatDateForBackend = (dateStr) => {
            if (!dateStr) return '';
            if (dateStr.includes('-') && dateStr.split('-')[0].length === 4) return dateStr; // Already YYYY-MM-DD
            const parts = dateStr.split('/');
            if (parts.length === 3) {
                if (parts[0].length === 4) {
                    return `${parts[0]}-${parts[1]}-${parts[2]}`;
                }
                return `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
            return dateStr;
        };

        const dateFields = ['registered_on', 'valid_till', 'last_renewed_on', 'date_of_birth', 'membership_payment_date', 'leadership_payment_date'];

        Object.keys(formData).forEach(key => {
            // Check if value is not null/undefined. Allow empty strings if user cleared field.
            if (formData[key] !== null && formData[key] !== undefined && key !== 'picture') {
                if (dateFields.includes(key) && formData[key]) {
                    data.append(key, formatDateForBackend(formData[key]));
                } else if (key === 'slug' && typeof formData[key] === 'string') {
                    // Sanitize slug by removing leading/trailing slashes
                    data.append(key, formData[key].replace(/^\/|\/$/g, ''));
                } else {
                     data.append(key, formData[key] === null ? '' : formData[key]);
                }
            }
        });
        if (picture) {
            data.append('picture', picture);
        }

        // Append Renewals
        const validRenewals = renewals
            .filter(r => r.amount && String(r.amount).trim() !== '')
            .map(r => ({
                amount: r.amount,
                payment_date: formatDateForBackend(r.payment_date) || null
            }));

        if (validRenewals.length > 0) {
            data.append('renewals', JSON.stringify(validRenewals));
        }

        try {
            await axios.patch(`nai-members/members/${memberId}/`, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setStatus({ type: 'success', message: 'Member updated successfully!' });
            setTimeout(onUpdate, 1500);
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : 'Failed to update member.';
            setStatus({ type: 'error', message: errorMsg });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div>Loading member details...</div>;

    return (
        <div className="bg-white rounded-lg shadow p-6 relative">
            <ToastContainer position="top-right" autoClose={3000} />
            <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900 mb-6 font-medium">
                <MdArrowBack className="mr-2" /> Back
            </button>
            
            <h2 className="text-xl font-bold text-gray-800 mb-6">Edit Member</h2>

            {status && (
                <div className={`p-4 mb-4 rounded ${status.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} break-words`}>
                    {status.message}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8 max-w-6xl">
                
                {/* 1. Basic Information */}
                <section>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <InputField label="Member Name" name="member_name" required={true} value={formData.member_name} onChange={handleChange} />
                        <InputField label="NAI Member ID (Unique)" name="member_id" type="number" required={true} value={formData.member_id} onChange={handleChange} />
                        <InputField label="DMCR Member Code" name="member_code" value={formData.member_code} onChange={handleChange} />
                        
                        <div className="flex items-center space-x-2 mt-6">
                            <input 
                                type="checkbox" 
                                id="is_association_ambassador" 
                                name="is_association_ambassador"
                                checked={formData.is_association_ambassador}
                                onChange={(e) => setFormData({ ...formData, is_association_ambassador: e.target.checked })}
                                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                            />
                            <label htmlFor="is_association_ambassador" className="text-sm font-bold text-blue-700 cursor-pointer uppercase">
                                Association Ambassador
                            </label>
                        </div>
                        
                        <SelectField label="Member Type" name="member_type" options={memberTypeChoices} required={true} value={formData.member_type} onChange={handleChange} />
                        <SelectField label="Designation / Role" name="member_type_choice" options={memberRoleChoices} required={true} value={formData.member_type_choice} onChange={handleChange} />
                        <SelectField label="Status" name="member_status" options={memberStatusChoices} required={true} value={formData.member_status} onChange={handleChange} />
                        <InputField label="Profile URL Slug (Optional)" name="slug" value={formData.slug} onChange={handleChange} placeholder="member/name" />
                    </div>
                    
                    {((formData.member_type || '').toUpperCase() === "LEADER") && (
                        <div className="mt-6 p-4 bg-blue-50 rounded-md border border-blue-100">
                            <h4 className="text-md font-medium text-blue-900 mb-4">Leadership Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <SelectField 
                                    label="Place" 
                                    name="leadership_place" 
                                    options={placeChoices} 
                                    value={formData.leadership_place} 
                                    onChange={handleChange} 
                                />
                                <SelectField 
                                    label="Designation" 
                                    name="leadership_designation" 
                                    options={designationChoices} 
                                    value={formData.leadership_designation} 
                                    onChange={handleChange} 
                                />
                                <SelectField 
                                    label="Wing" 
                                    name="leadership_wing" 
                                    options={wingChoices} 
                                    value={formData.leadership_wing} 
                                    onChange={handleChange} 
                                />
                            </div>
                        </div>
                    )}
                </section>

                {/* 2. Membership Timeline */}
                <section>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">Membership Timeline</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <InputField label="Registered On" name="registered_on" type="date" value={formatDateToYYYYMMDD(formData.registered_on)} onChange={handleChange} />
                        <InputField label="Valid Till" name="valid_till" type="date" value={formatDateToYYYYMMDD(formData.valid_till)} onChange={handleChange} />
                        <InputField label="Last Renewed On" name="last_renewed_on" type="date" value={formatDateToYYYYMMDD(formData.last_renewed_on)} onChange={handleChange} />
                        
                        <InputField label="Member Age" name="member_age" value={formData.member_age} onChange={handleChange} placeholder="e.g. 5 Years" />
                        <InputField label="Membership Age" name="membership_age" value={formData.membership_age} onChange={handleChange} placeholder="e.g. 2 Years" />
                        <SelectField label="Renewal Status" name="renewal" options={renewalChoices} value={formData.renewal} onChange={handleChange} />
                    </div>
                </section>

                {/* 3. Contact Details */}
                <section>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">Contact Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <InputField label="Mobile Number" name="mobile" type="number" required={true} value={formData.mobile} onChange={handleChange} />
                        <InputField label="WhatsApp Number" name="whatsapp_no" type="number" value={formData.whatsapp_no} onChange={handleChange} />
                        <InputField label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} />
                        <InputField label="Father / Husband Name" name="father_or_husband_name" value={formData.father_or_husband_name} onChange={handleChange} />
                        <InputField label="Blood Group" name="blood_group" value={formData.blood_group} onChange={handleChange} />
                        <InputField label="Education" name="education" value={formData.education} onChange={handleChange} />
                        <div className="col-span-1 md:col-span-3">
                            <label className="block text-sm font-medium text-gray-700">
                                Address <span className="text-red-500">*</span>
                            </label>
                            <textarea name="member_address" rows={2} required value={formData.member_address} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" />
                        </div>
                    </div>
                </section>

                {/* 4. Location Details */}
                <section>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">Location Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <SelectField label="State" name="state" options={indianStates} required={true} value={formData.state} onChange={handleChange} />
                        <InputField label="District" name="district" required={true} value={formData.district} onChange={handleChange} />
                        <InputField label="City" name="city" required={true} value={formData.city} onChange={handleChange} />
                        <InputField label="Sub District" name="sub_district" value={formData.sub_district} onChange={handleChange} />
                        
                        <InputField label="Zone" name="zone" value={formData.zone} onChange={handleChange} />
                        <InputField label="Area Name" name="areaname" value={formData.areaname} onChange={handleChange} />
                        <InputField label="Taluk" name="taluk" value={formData.taluk} onChange={handleChange} />
                        <InputField label="Village" name="village" value={formData.village} onChange={handleChange} />
                        
                        <InputField label="Ward" name="ward" value={formData.ward} onChange={handleChange} />
                        <InputField label="Pin Code" name="pin_code" required={true} value={formData.pin_code} onChange={handleChange} />
                        <InputField label="Police Station" name="police_station" value={formData.police_station} onChange={handleChange} />
                    </div>
                </section>

                {/* 5. Personal Details */}
                <section>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">Personal & Identity Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <SelectField label="Gender" name="gender" options={genderChoices} required={true} value={formData.gender} onChange={handleChange} />
                        <InputField label="Date of Birth" name="date_of_birth" type="date" value={formatDateToYYYYMMDD(formData.date_of_birth)} onChange={handleChange} />
                        <InputField label="Religion" name="religion" value={formData.religion} onChange={handleChange} />
                        <InputField label="Voter ID" name="voter_id" value={formData.voter_id} onChange={handleChange} />
                        <InputField label="Aadhar Number" name="aadhar_number" value={formData.aadhar_number} onChange={handleChange} />
                        <InputField label="Occupation" name="occupation" value={formData.occupation} onChange={handleChange} />
                        <InputField label="Business" name="business" value={formData.business} onChange={handleChange} />
                        <InputField label="Business Designation" name="business_designation" value={formData.business_designation} onChange={handleChange} />
                    </div>
                </section>

                {/* Unified Section: Add/View Payments */}
                <section>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">Payment Details</h3>
                    
                    {/* Existing History */}
                    <div className="mb-6">
                        <h4 className="text-md font-semibold text-gray-700 mb-2">Payment History (Read Only)</h4>
                        <div className="overflow-x-auto border rounded-md shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fee Type</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment Type</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount Paid</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Balance Due</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {existingPayments.length > 0 ? (
                                        existingPayments.map((payment) => (
                                            <tr key={payment.id}>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-gray-900">{formatPaymentType(payment.payment_type)}</td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                                                    {payment.payment_status_type === 'part' ? (
                                                        <span className="px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded bg-amber-100 text-amber-800">
                                                            Part Payment
                                                        </span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded bg-green-100 text-green-800">
                                                            Full Payment
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm font-bold text-gray-900">₹{payment.amount}</td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                                                    {payment.payment_status_type === 'part' && payment.balance_amount ? (
                                                        <span className="text-red-600 font-semibold">₹{payment.balance_amount}</span>
                                                    ) : '-'}
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{formatDateToDDMMYYYY(payment.payment_date)}</td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{payment.payment_mode || '-'}</td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        payment.status === 'success' ? 'bg-green-100 text-green-800' : 
                                                        payment.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                        {payment.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="px-4 py-3 text-sm text-gray-500 text-center">No payment history found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Add New Payment Form */}
                    <div className="p-5 bg-blue-50/50 rounded-lg border border-blue-200 shadow-sm">
                        <div className="mb-4">
                            <h4 className="text-md font-bold text-gray-800">Add New Payment</h4>
                            <p className="text-sm text-gray-500">Record a new payment for this member. Once added, it will immediately appear in the Payment History table above.</p>
                        </div>

                        <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                {/* Member Type / Fee Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Fee Category / Member Type <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={newPaymentForm.payment_type}
                                        onChange={(e) => handleNewPaymentChange('payment_type', e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm bg-white"
                                    >
                                        {feeCategoryOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Payment Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Payment Type <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={newPaymentForm.payment_status_type}
                                        onChange={(e) => handleNewPaymentChange('payment_status_type', e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm bg-white"
                                    >
                                        {paymentStatusTypeOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Mode of Payment */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Mode of Payment <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={newPaymentForm.payment_mode}
                                        onChange={(e) => handleNewPaymentChange('payment_mode', e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm bg-white"
                                    >
                                        {paymentModeOptions.map(mode => (
                                            <option key={mode} value={mode}>{mode}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Payment Date */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Payment Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formatDateToYYYYMMDD(newPaymentForm.payment_date)}
                                        onChange={(e) => handleNewPaymentChange('payment_date', e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm bg-white"
                                    />
                                </div>
                            </div>

                            {/* Amount & Balance Fields */}
                            {newPaymentForm.payment_status_type === 'part' ? (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-amber-50 rounded-md border border-amber-200">
                                    <div>
                                        <label className="block text-sm font-medium text-amber-900">
                                            Total Amount (₹) <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="e.g. 5000"
                                            value={newPaymentForm.total_amount}
                                            onChange={(e) => handleNewPaymentChange('total_amount', e.target.value)}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-amber-900">
                                            Amount Paid (₹) <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="e.g. 2000"
                                            value={newPaymentForm.amount}
                                            onChange={(e) => handleNewPaymentChange('amount', e.target.value)}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-amber-900">
                                            Balance Amount (₹) <span className="text-xs text-amber-700">(Auto-calculated)</span>
                                        </label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={newPaymentForm.balance_amount !== '' ? `₹ ${newPaymentForm.balance_amount}` : '-'}
                                            className="mt-1 block w-full rounded-md border-amber-300 bg-amber-100 text-amber-900 font-bold p-2 text-sm cursor-not-allowed"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Amount Paid (₹) <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="e.g. 1000"
                                            value={newPaymentForm.amount}
                                            onChange={(e) => handleNewPaymentChange('amount', e.target.value)}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Notes / Remarks (Optional)</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Reference / Cheque No / Purpose"
                                            value={newPaymentForm.notes}
                                            onChange={(e) => handleNewPaymentChange('notes', e.target.value)}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm bg-white"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Action Button to Add Payment */}
                            <div className="flex justify-end pt-2">
                                <button
                                    type="button"
                                    onClick={handleAddPaymentDirect}
                                    disabled={addingPayment}
                                    className={`inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-semibold rounded-md shadow-sm text-white ${addingPayment ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition`}
                                >
                                    {addingPayment ? 'Adding Payment...' : '+ Add Payment'}
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 6. Referral Information */}
                <section>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">Referral Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Referrer Search (by Name, ID, or Mobile)
                            </label>
                            <input 
                                type="text"
                                name="referrer_name"
                                value={formData.referrer_name || ''}
                                onChange={(e) => handleReferralSearch(e.target.value)}
                                onFocus={() => formData.referrer_name && handleReferralSearch(formData.referrer_name)}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                placeholder="Type to search members..."
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2" 
                            />
                            
                            {showSuggestions && referralSuggestions.length > 0 && (
                                <ul className="absolute z-10 mt-1 w-[48%] bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                    {referralSuggestions.map((member) => (
                                        <li 
                                            key={member.id}
                                            onClick={() => selectReferrer(member)}
                                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-600 hover:text-white group"
                                        >
                                            <div className="flex flex-col">
                                                <span className="font-semibold block truncate">
                                                    {member.member_name} (ID: {member.member_id ? String(member.member_id).padStart(4, '0') : ''})
                                                </span>
                                                <span className="text-gray-500 group-hover:text-blue-200 text-xs">
                                                    {member.mobile} • {member.district}
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        
                        <InputField label="Referrer Mobile" name="referrer_mobile_number" type="number" required={false} value={formData.referrer_mobile_number || ''} onChange={handleChange} />
                    </div>
                </section>

                {/* 7. Other */}
                <section>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">Other Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <SelectField label="Know us from" name="know_us_from" options={knowUsFromChoices} value={formData.know_us_from || ''} onChange={handleChange} />
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Remarks</label>
                            <textarea name="remarks" rows={3} value={formData.remarks} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Profile Picture</label>
                            {formData.picture && !picture && (
                                <div className="mb-2 flex items-center space-x-3">
                                    <img src={formData.picture} alt="Current Profile" className="h-16 w-16 object-cover rounded-md border" />
                                    <span className="text-xs text-gray-500">Current photo</span>
                                </div>
                            )}
                            {picture && (
                                <div className="mb-2 flex items-center space-x-3">
                                    <img src={URL.createObjectURL(picture)} alt="New Preview" className="h-16 w-16 object-cover rounded-md border border-blue-500" />
                                    <span className="text-xs text-blue-600 font-medium">New photo selected</span>
                                </div>
                            )}
                            <input type="file" accept="image/*" onChange={handleFileChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                        </div>
                    </div>
                </section>

                <div className="pt-4">
                    <button 
                        type="submit" 
                        disabled={submitting}
                        className={`w-full md:w-auto flex justify-center py-3 px-8 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${submitting ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                    >
                        {submitting ? 'Updating Member...' : 'Update Member'}
                    </button>
                </div>
            </form>
        </div>
    );
};


export default EditMember;
